import React, { useState, useEffect, useCallback } from 'react';
import { getRiskTemplates, updateRiskTemplate, getDimensions } from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';

// 退休 level/score_bands 后的固定行编辑器：每个通用维度一行，只调 阈值 + 文案内核 + 启停。
// 后端: apps/enterprises/admin_risk_template_views.py（IsAdminRole）。
// 严重度不在此设——由 分数/阈值 实时分 5 档语气寄存器（risk_registers）决定。
// 前置不变量：8 通用行齐全由部署时 reset_v5_risk_fallbacks 兜底（缺行不在面板补，见 plan §9）。

const ALLOWED = new Set(['name', 'score', 'position']);

function placeholderErr(text) {
  const matches = (text || '').match(/\{(\w+)\}/g) || [];
  const unknown = matches.map((m) => m.slice(1, -1)).filter((k) => !ALLOWED.has(k));
  return unknown.length ? `含未支持占位符: ${[...new Set(unknown)].join(', ')}（仅 name/score/position）` : null;
}

export default function AdminRiskTemplatesPage() {
  const [rows, setRows] = useState([]);     // 拉取的通用模板，每行附 draft 编辑态
  const [dimMap, setDimMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getRiskTemplates({ industry: '通用' });
      const list = Array.isArray(data) ? data : (data.results ?? []);
      setRows(list.map((t) => ({
        id: t.id,
        dimension_code: t.dimension_code,
        dimension_name: t.dimension_name,
        threshold: t.threshold,
        template_text: t.template_text,
        is_active: t.is_active,
      })));
    } catch (e) {
      setError(e.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getDimensions().then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.results ?? []);
      const m = {};
      list.forEach((d) => { m[d.code] = d.name; });
      setDimMap(m);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const saveRow = async (row) => {
    if (row.threshold < 0 || row.threshold > 100) { setError('阈值必须在 0-100 之间'); return; }
    if (!row.template_text?.trim()) { setError('文案内核不能为空'); return; }
    const pe = placeholderErr(row.template_text);
    if (pe) { setError(pe); return; }
    setSavingId(row.id); setError(''); setSuccess('');
    try {
      await updateRiskTemplate(row.id, {
        threshold: row.threshold,
        template_text: row.template_text,
        is_active: row.is_active,
      });
      setSuccess(`已保存：${dimMap[row.dimension_code] || row.dimension_code}`);
    } catch (e) {
      const data = e.response?.data || {};
      const firstField = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k][0]);
      setError(firstField ? `${firstField}: ${data[firstField][0]}` : (data.detail || '保存失败'));
    } finally { setSavingId(null); }
  };

  const dimName = (r) => r.dimension_name || dimMap[r.dimension_code] || r.dimension_code;

  return (
    <AdminLayout title="风险模板">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ marginBottom: 16, fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
        每个能力维度设一条触发<b>阈值</b>与<b>文案内核</b>：学员该维度得分 &lt; 阈值时触发风险项。
        严重度语气由 <code style={inlineCode}>分数 / 阈值</code> 比值实时分 5 档（极低 → 略低），
        无需在此设等级。文案支持 <code style={inlineCode}>{'{name}'}</code> / <code style={inlineCode}>{'{score}'}</code> / <code style={inlineCode}>{'{position}'}</code> 占位符（缺值静默回退空串）。
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>加载中...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            暂无通用风险模板（请联系运维跑 reset_v5_risk_fallbacks 补齐 8 通用行）
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['维度', '阈值', '文案内核', '启用', '操作'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} style={{
                  borderBottom: idx < rows.length - 1 ? '1px solid #f1f5f9' : 'none',
                  opacity: r.is_active ? 1 : 0.6,
                }}>
                  <td style={{ ...tdStyle, minWidth: 160 }}>
                    <div style={{ fontWeight: 600, color: '#1a3a6b' }}>{dimName(r)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{r.dimension_code}</div>
                  </td>
                  <td style={{ ...tdStyle, width: 100 }}>
                    <input type="number" min="0" max="100" step="1" style={{ ...inputStyle, width: 80 }}
                      value={r.threshold}
                      onChange={(e) => patchRow(r.id, { threshold: parseFloat(e.target.value) || 0 })} />
                  </td>
                  <td style={tdStyle}>
                    <textarea
                      style={{ ...inputStyle, height: 88, resize: 'vertical', fontFamily: 'inherit' }}
                      value={r.template_text}
                      onChange={(e) => patchRow(r.id, { template_text: e.target.value })} />
                  </td>
                  <td style={{ ...tdStyle, width: 64, textAlign: 'center' }}>
                    <input type="checkbox" checked={r.is_active}
                      onChange={(e) => patchRow(r.id, { is_active: e.target.checked })} />
                  </td>
                  <td style={{ ...tdStyle, width: 90 }}>
                    <Button onClick={() => saveRow(r)} disabled={savingId === r.id}>
                      {savingId === r.id ? '保存中' : '保存'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
        共 {rows.length} 条通用维度模板。保存后立即清空风险预警缓存，下次看板访问重算。
      </p>
    </AdminLayout>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#475569', borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'top', color: '#334155' };
const inlineCode = {
  fontFamily: 'ui-monospace, monospace', fontSize: 11,
  background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#1a3a6b',
};
